import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import {
  isOwnTransportIngredient,
  isStayIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type { UserLocation } from '@apps/main/new-desktop/core/models';
import { ItinerarySegmentPlaceholderType } from '@apps/main/new-desktop/modules/itineraryButton/ItinerarySegmentPlaceholder.model';

import { isFeatureToggleActive } from '@libs/feature-toggle';
import {
  ConnectionType,
  IngredientType,
  TransportType,
} from '@libs/trip-planner-interfaces';
import {
  isHotelIngredientResponseWithStaticInfo,
  isOwnStayIngredientResponseWithStaticInfo,
  isOwnTransportIngredientResponseWithStaticInfo,
  isTransportIngredientResponseWithStaticInfo,
} from '@libs/search-service-smart-search';
import type { ChronologicalItinerary } from '@libs/trip-planner-interfaces';

import type {
  ItineraryLocationSegment,
  ItineraryOwnTransportSegment,
  ItinerarySegment,
  ItinerarySimpleTransportSegmentType,
  LocationSegment,
  GetTripOverviewSegmentsParams,
} from '../../models';
import { ItinerarySegmentType, UnavailabilityReason } from '../../models';

import { getBookableTransportSegment } from './tripOverviewBookableTransportSegmentLogic';
import { getHotelRelatedSegments } from './tripOverviewHotelRelatedSegmentsLogic';
import { getOwnStayRelatedSegments } from './tripOverviewOwnStayRelatedSegmentsLogic';
import { getOwnTransportRelatedSegment } from './tripOverviewOwnTransportRelatedSegmentsLogic';
import { getCityText } from './tripOverviewLocationSegmentsLogic';

export function getTripOverviewSegments(
  params: GetTripOverviewSegmentsParams
): ItinerarySegment[] {
  // Chronological itinerary is empty when there is no selectedItineraryWithStaticInfo
  if (!params.chronologicalItinerary?.length) {
    return [];
  }
  const itinerary = params.chronologicalItinerary.reduce(
    (
      segments: ItinerarySegment[],
      itineraryByPeriod: ChronologicalItinerary,
      currentIndex: number
    ) => {
      itineraryByPeriod.ingredients.forEach((ingredient) => {
        const bookableTransportSegment = getBookableTransportSegment(
          ingredient,
          params.getIngredient,
          params.isSuggestedTransport,
          params.ferryTicketCategoryDictionary,
          params.userSelectedFerryTicketCategory,
          params.getFlightAllCarriers,
          params.getFerryAllCarriers,
          params.currency,
          params.selectedDate
        );
        if (bookableTransportSegment) {
          segments.push(bookableTransportSegment);
        }
        const hotelRelatedSegments = getHotelRelatedSegments(
          ingredient,
          params.getIngredient,
          params.isSuggestedHotel,
          params.areThereActivitiesInDestination(ingredient.ingredientId),
          params.getActivitySuggestions(ingredient.ingredientId),
          params.getStaticActivitySuggestions(ingredient.ingredientId),
          params.getIngredientIndex(ingredient.ingredientId),
          params.moneyFormanterFn,
          itineraryByPeriod,
          params.selectedDate
        );
        segments.push(...hotelRelatedSegments);

        let ownTransportRelatedSegments:
          | ItineraryOwnTransportSegment
          | undefined;

        if (isOwnTransportIngredientResponseWithStaticInfo(ingredient)) {
          const hotelRelatedSegment = itineraryByPeriod.ingredients[1];
          const isLastSegment =
            ingredient.ingredientId ===
            itineraryByPeriod.ingredients[
              itineraryByPeriod.ingredients.length - 1
            ]?.ingredientId;

          if (
            hotelRelatedSegment &&
            (isHotelIngredientResponseWithStaticInfo(hotelRelatedSegment) ||
              isOwnStayIngredientResponseWithStaticInfo(hotelRelatedSegment))
          ) {
            const hasZeroNumberOfNights = !hotelRelatedSegment.numberOfNights;

            ownTransportRelatedSegments = getOwnTransportRelatedSegment(
              ingredient,
              hotelRelatedSegment,
              params.getIngredientIndex(ingredient.ingredientId),
              params.getIngredient,
              params.calculatedRoute,
              hasZeroNumberOfNights ||
                getShouldAddTransportSegment(
                  itineraryByPeriod,
                  params.chronologicalItinerary[currentIndex - 1]
                ),
              params.isUserFromBookingPage,
              params.hasUserSelectedTransport,
              false
            );
          } else if (isLastSegment && params.endLocationIngredient) {
            const previousIngredient =
              params.chronologicalItinerary[currentIndex - 1];
            const previousDestinationId = previousIngredient?.ingredients.find(
              (ingredient) =>
                ingredient.ingredientType === IngredientType.Hotel ||
                ingredient.ingredientType === IngredientType.OwnStay
            )?.ingredientId;
            const previousDestination = previousDestinationId
              ? params.getIngredient(previousDestinationId)
              : undefined;
            const previousIngredientDestination =
              previousDestination && isStayIngredient(previousDestination)
                ? getCityText(previousDestination.displayText)
                : undefined;

            ownTransportRelatedSegments = getOwnTransportRelatedSegment(
              ingredient,
              params.endLocationIngredient,
              params.getIngredientIndex(ingredient.ingredientId),
              params.getIngredient,
              params.calculatedRoute,
              getShouldAddTransportSegment(
                itineraryByPeriod,
                params.chronologicalItinerary[currentIndex - 1]
              ),
              params.isUserFromBookingPage,
              params.hasUserSelectedTransport,
              true,
              previousIngredientDestination
            );
          }

          if (ownTransportRelatedSegments) {
            segments.push(ownTransportRelatedSegments);
          }
        }
        const ownStayRelatedSegments = getOwnStayRelatedSegments({
          ingredient: ingredient,
          getIngredient: params.getIngredient,
          ingredientIndex: params.getIngredientIndex(ingredient.ingredientId),
          areThereActivitiesInDestination:
            params.areThereActivitiesInDestination(ingredient.ingredientId),
          activitySuggestions: params.getActivitySuggestions(
            ingredient.ingredientId
          ),
          staticActivitySuggestions: params.getStaticActivitySuggestions(
            ingredient.ingredientId
          ),
          moneyFormanterFn: params.moneyFormanterFn,
          itineraryByPeriod,
          selectedDate: params.selectedDate,
        });
        segments.push(...ownStayRelatedSegments);
      });
      if (isFeatureToggleActive('show-availability-errors')) {
        addErrorReasons(segments);
      }

      return segments;
    },
    []
  );

  const firstIngredient = params.chronologicalItinerary[0]?.ingredients[0];

  if (firstIngredient) {
    const firstRecipeIngredient = params.getIngredient(
      firstIngredient.ingredientId
    );
    if (
      params.startLocation &&
      firstRecipeIngredient &&
      isOwnTransportIngredient(firstRecipeIngredient) &&
      firstRecipeIngredient.ownTransportType !== TransportType.None
    ) {
      itinerary.unshift(
        getTripOverviewLocationSegment(
          params.startLocation,
          ItinerarySegmentType.StartLocation
        )
      );
    }
  }

  const endLocationSegments = getEndLocationSegments(
    params.endLocation,
    params.endLocationIngredient,
    params.chronologicalItinerary
  );
  itinerary.push(...endLocationSegments);
  return itinerary;
}

export function getEndLocationSegments(
  endLocation: UserLocation | undefined,
  endLocationIngredient: HotelIngredient | undefined,
  chronologicalItinerary: ChronologicalItinerary[] = []
): ItinerarySegment[] {
  const segments: ItinerarySegment[] = [];

  const lastChronologicalItinerary =
    chronologicalItinerary[chronologicalItinerary.length - 1];

  const lastItineraryIngredient =
    lastChronologicalItinerary &&
    lastChronologicalItinerary.ingredients[
      lastChronologicalItinerary.ingredients.length - 1
    ];
  if (
    endLocation &&
    endLocationIngredient &&
    lastItineraryIngredient &&
    isOwnTransportIngredientResponseWithStaticInfo(lastItineraryIngredient) &&
    lastItineraryIngredient.ownTransportType !== TransportType.None
  ) {
    segments.push(
      getTripOverviewLocationSegment(
        endLocation,
        ItinerarySegmentType.EndLocation
      )
    );
  }
  return segments;
}

export function getTripOverviewLocationSegment(
  location: UserLocation,
  segmentType:
    | ItinerarySegmentType.StartLocation
    | ItinerarySegmentType.EndLocation
): ItineraryLocationSegment {
  return {
    segmentType,
    location: getLocationSegmentDisplay(location),
  };
}

function getLocationSegmentDisplay(location: UserLocation): LocationSegment {
  return {
    name: location.name,
    country: location.country,
  };
}

export function getTransportTypeForItinerarySimpleTransportSegment(
  segmentType: ItinerarySimpleTransportSegmentType
): TransportType {
  switch (segmentType) {
    case ItinerarySegmentType.Car:
      return TransportType.Car;
    case ItinerarySegmentType.Walk:
      return TransportType.Walk;
    case ItinerarySegmentType.Cycle:
      return TransportType.Bicycle;
    default:
      throw new Error('No simple transport in itinerary segment');
  }
}

export function getTransportSegmentPlaceholderForItinerarySimpleTransportSegment(
  transportType: ItinerarySimpleTransportSegmentType
): ItinerarySegmentPlaceholderType {
  switch (transportType) {
    case ItinerarySegmentType.Car:
      return ItinerarySegmentPlaceholderType.Car;
    case ItinerarySegmentType.Walk:
      return ItinerarySegmentPlaceholderType.Walk;
    case ItinerarySegmentType.Cycle:
      return ItinerarySegmentPlaceholderType.Bicycle;
    default:
      throw new Error('No simple transport in itinerary segment');
  }
}

export function mapTransportTypeToConnectionType(
  transportType: TransportType
): ConnectionType {
  switch (transportType) {
    case TransportType.Car:
      return ConnectionType.CAR;
    case TransportType.Walk:
      return ConnectionType.WALK;
    case TransportType.Bicycle:
      return ConnectionType.BICYCLE;
    case TransportType.Flight:
      return ConnectionType.FLIGHT;
    case TransportType.Ferry:
      return ConnectionType.FERRY;
    default:
      return ConnectionType.NONE;
  }
}

export function getShouldAddTransportSegment(
  itineraryByPeriod: ChronologicalItinerary,
  previousChronologicalItinerary: ChronologicalItinerary | undefined
): boolean {
  const hasAdvancedTransportInPeriod = itineraryByPeriod.ingredients.some(
    isTransportIngredientResponseWithStaticInfo
  );

  if (hasAdvancedTransportInPeriod) {
    return false;
  }

  if (
    previousChronologicalItinerary &&
    previousChronologicalItinerary.ingredients.length === 1
  ) {
    const ingredient = previousChronologicalItinerary.ingredients[0];

    return (
      !!ingredient && !isTransportIngredientResponseWithStaticInfo(ingredient)
    );
  }
  return true;
}

function addErrorReasons(segment: ItinerarySegment[]): void {
  segment.forEach((item) => {
    if ('reasons' in item) {
      item.reasons = [
        UnavailabilityReason.SelectedDate,
        UnavailabilityReason.SelectedProduct,
        UnavailabilityReason.SelectedFilters,
        UnavailabilityReason.Suggested,
      ];
    }
  });
}
